import { useState } from "react";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { COMPANY, WHATSAPP_URL } from "@/lib/constants";
import { submitEnquiry } from "@/lib/api";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return toast.error("Name and phone are required");
    setSubmitting(true);
    try {
      await submitEnquiry(form);
      toast.success("Message sent! Our team will contact you shortly.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch { toast.error("Something went wrong."); }
    setSubmitting(false);
  };

  return (
    <div className="pt-28 pb-24 bg-brand-ivory">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <Reveal>
          <div className="text-center mb-14">
            <div className="divider-gold">Contact Us</div>
            <h1 className="mt-4 font-serif text-5xl lg:text-6xl text-brand-navy">We'd love to hear from you</h1>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <Reveal>
            <div className="bg-brand-navy text-white p-8">
              <h3 className="font-serif text-2xl">Get in touch</h3>
              <div className="mt-6 space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-brand-gold/20 border border-brand-gold/40 flex items-center justify-center text-brand-gold flex-shrink-0"><MapPin size={16} /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-brand-gold">Address</div>
                    <div className="text-sm text-brand-ivory/85 mt-1">{COMPANY.address}</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-brand-gold/20 border border-brand-gold/40 flex items-center justify-center text-brand-gold flex-shrink-0"><Phone size={16} /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-brand-gold">Phone</div>
                    <a href={`tel:${COMPANY.phone}`} className="text-sm text-brand-ivory/85 mt-1 hover:text-brand-gold">{COMPANY.phone}</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-brand-gold/20 border border-brand-gold/40 flex items-center justify-center text-brand-gold flex-shrink-0"><Mail size={16} /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-brand-gold">Email</div>
                    <a href={`mailto:${COMPANY.email}`} className="text-sm text-brand-ivory/85 mt-1 hover:text-brand-gold">{COMPANY.email}</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-brand-gold/20 border border-brand-gold/40 flex items-center justify-center text-brand-gold flex-shrink-0"><MessageCircle size={16} /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-brand-gold">WhatsApp</div>
                    <a href={WHATSAPP_URL()} target="_blank" rel="noreferrer" className="text-sm text-brand-ivory/85 mt-1 hover:text-brand-gold">Chat with us</a>
                  </div>
                </div>
              </div>
              <div className="mt-8 aspect-video bg-black/30 overflow-hidden">
                <iframe title="map" src="https://www.google.com/maps?q=Sonam+Plaza+Nemawar+Road+Navlakha+Indore&output=embed" className="w-full h-full border-0" loading="lazy" />
              </div>
            </div>
          </Reveal>

          <Reveal delay={150}>
            <form onSubmit={submit} className="bg-white border border-brand-gold/20 p-8 space-y-4">
              <h3 className="font-serif text-2xl text-brand-navy">Send us a message</h3>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Full Name*</label>
                <Input required data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Phone*</label>
                  <Input required data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm mt-1" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Email</label>
                  <Input data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm mt-1" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Message</label>
                <Textarea rows={5} data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-sm mt-1" />
              </div>
              <Button type="submit" disabled={submitting} data-testid="contact-submit" className="w-full h-12 rounded-sm bg-brand-navy text-brand-gold hover:bg-brand-navy/90">{submitting ? "Sending..." : "Send Message"}</Button>
            </form>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
